import { Box, Button, Card, CardContent, Chip, Typography } from '@mui/material';

import type { MonthlyDPH, NotificationFrequency } from '@/types';

import { findNotificationFrequency } from '../utils/dph.utils';
import { UsageDoughnutChart } from './UsageDoughnutChart';

interface MonthlyCardProps {
  data: MonthlyDPH;
  setType: (type: string) => void;
  setShowHistoryModal: (open: boolean) => void;
  setEditModal: (open: boolean) => void;
  notificationFrequency: NotificationFrequency[];
}

/**
 * Card displaying current month DPH summary
 * Shows month name, usage doughnut chart, and notification settings
 * Includes button to show history modal
 */
export const MonthlyCard = ({
  data,
  setType,
  setShowHistoryModal,
  setEditModal,
  notificationFrequency,
}: MonthlyCardProps) => {
  // Check if data exists
  if (!data) {
    return null;
  }

  const { months, notificationEmails } = data;

  // Get the latest month data
  if (!months || months.length === 0) {
    return null;
  }

  const latestMonth = months[months.length - 1];

  // Check if latestMonth exists before destructuring
  if (!latestMonth) {
    return null;
  }

  const {
    month,
    dataProcHours,
    usedDataProcHours,
    usedDataProcHoursPercentage,
    availableDataProcHours,
    availableDataProcHoursPercentage,
    threshold,
    thresholdPercentage,
    thresholdValueHours,
    thresholdRemainingHours,
    notificationFrequencyId,
  } = latestMonth;

  return (
    <Card
      onClick={() => setType('Monthly')}
      sx={{
        cursor: 'pointer',
        '&:hover': {
          boxShadow: 3,
        },
      }}
    >
      <CardContent>
        <Typography variant="h6" gutterBottom>
          <strong>Monthly Summary</strong>
        </Typography>

        {/* Month Info */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>🗓️ Month:</strong> {month}
          </Typography>
        </Box>

        {/* Usage Chart */}
        <Box sx={{ width: 300, mx: 'auto', my: 3 }}>
          <UsageDoughnutChart
            dataProcHours={dataProcHours}
            usedDataProcHours={usedDataProcHours}
            usedDataProcHoursPercentage={usedDataProcHoursPercentage}
            availableDataProcHours={availableDataProcHours}
            availableDataProcHoursPercentage={availableDataProcHoursPercentage}
            threshold={threshold}
            thresholdPercentage={thresholdPercentage}
            thresholdValueHours={thresholdValueHours}
            thresholdRemainingHours={thresholdRemainingHours}
          />
        </Box>

        {/* Show History Button - Fixed height to align with Licensed Period card */}
        <Box
          sx={{
            minHeight: 68,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
          }}
        >
          <Button variant="text" onClick={() => setShowHistoryModal(true)}>
            <strong>Show History</strong>
          </Button>
        </Box>

        {/* Notification Settings */}
        <Card
          variant="outlined"
          sx={{
            p: 2,
            borderColor: 'grey.400',
          }}
        >
          <Typography variant="subtitle2" gutterBottom sx={{ borderBottom: 'none' }}>
            Notifications
          </Typography>

          <Typography variant="body2" component="div" sx={{ mb: 2 }}>
            Threshold set at <strong>{thresholdPercentage}%</strong> of{' '}
            <strong>{dataProcHours}</strong> hours at{' '}
            <Chip
              label={findNotificationFrequency(notificationFrequency, notificationFrequencyId)}
              size="small"
              sx={{ ml: 0.5 }}
            />
          </Typography>

          <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold' }}>
            Emails
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
            {notificationEmails &&
              notificationEmails.map((email) => (
                <Chip key={email} label={email} size="small" variant="outlined" />
              ))}
          </Box>

          <Button variant="contained" onClick={() => setEditModal(true)} size="small">
            Edit
          </Button>
        </Card>
      </CardContent>
    </Card>
  );
};
